<template>
  <div class="trip-menu" :key="language">
    <!-- Trip Selector -->
    <div class="trip-selector">
      <ion-button 
        fill="clear" 
        @click="showTripList = true"
        class="trip-button"
      >
        <ion-icon :icon="mapOutline" slot="start"></ion-icon>
        <span class="trip-name">{{ currentTripName }}</span>
        <ion-icon :icon="chevronDownOutline" slot="end"></ion-icon>
      </ion-button>
    </div>

    <!-- Menu Button -->
    <ion-button 
      fill="clear" 
      @click="showMenu = true"
      title="Menu"
    >
      <ion-icon :icon="menuOutline"></ion-icon>
    </ion-button>

    <!-- Trip List Modal -->
    <AppPanelModal :is-open="showTripList" @did-dismiss="showTripList = false">
      <ion-header>
        <ion-toolbar>
          <ion-title>{{ t('myTrips') }}</ion-title>
          <ion-buttons slot="end">
            <ion-button @click="showTripList = false">
              <ion-icon :icon="closeOutline"></ion-icon>
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding">
        <!-- Empty State -->
        <div v-if="trips.length === 0" class="empty-state">
          <ion-icon :icon="mapOutline" class="empty-icon"></ion-icon>
          <h3 class="empty-title">{{ t('noTrips') }}</h3>
          <p class="empty-subtitle">{{ t('createFirstTrip') }}</p>
          <ion-button @click="showTripList = false; showNewTripModal = true">
            <ion-icon :icon="addOutline" slot="start"></ion-icon>
            {{ t('newTrip') }}
          </ion-button>
        </div>

        <!-- Trip Cards -->
        <div v-else class="trips-grid">
          <div
            v-for="trip in trips"
            :key="trip.id"
            class="trip-card"
            :class="{ 'selected': trip.id === currentTrip?.id }"
          >
            <div class="trip-card-content" @click="selectTrip(trip)">
              <!-- Icon + Title + Selected indicator -->
              <div class="trip-header">
                <div class="trip-icon-wrapper">
                  <ion-icon :icon="mapOutline" class="trip-icon"></ion-icon>
                </div>
                <div class="trip-info">
                  <h3 class="trip-title">{{ trip.name }}</h3>
                  <div class="trip-meta">
                    <span class="meta-item">
                      <ion-icon :icon="locationOutline" class="meta-icon"></ion-icon>
                      {{ trip.stops.length }}
                    </span>
                    <span class="meta-divider">•</span>
                    <span class="meta-item">
                      <ion-icon :icon="timeOutline" class="meta-icon"></ion-icon>
                      {{ formatDuration(trip.totalDuration || 0) }}
                    </span>
                    <span class="meta-divider">•</span>
                    <span class="meta-item">
                      <ion-icon :icon="navigateOutline" class="meta-icon"></ion-icon>
                      {{ formatDistance(trip.totalDistance || 0) }}
                    </span>
                  </div>
                </div>
                <div class="trip-actions">
                  <ion-icon 
                    v-if="trip.id === currentTrip?.id"
                    :icon="checkmarkCircleOutline"
                    class="selected-icon"
                  ></ion-icon>
                  <ion-button 
                    fill="clear" 
                    size="small"
                    @click.stop="confirmDeleteTrip(trip)"
                    color="danger"
                    class="delete-btn"
                  >
                    <ion-icon :icon="trashOutline" slot="icon-only"></ion-icon>
                  </ion-button>
                </div>
              </div>
            </div>

            <!-- Vehicle Selector for this trip -->
            <div v-if="vehicleStore.vehicles.length > 0" class="trip-vehicle-section" @click.stop>
              <div class="vehicle-label">
                <ion-icon :icon="carSportOutline" class="vehicle-icon"></ion-icon>
                <span>{{ t('vehicle') }}:</span>
              </div>
              <ion-select 
                :value="trip.vehicleId ?? 'none'"
                @ionChange="(e) => handleTripVehicleChange(trip.id, e.detail.value)"
                interface="popover"
                class="vehicle-select"
              >
                <ion-select-option value="none">{{ t('noVehicleSelected') }}</ion-select-option>
                <ion-select-option 
                  v-for="vehicle in vehicleStore.vehicles" 
                  :key="vehicle.id"
                  :value="vehicle.id"
                >
                  {{ vehicle.icon }} {{ vehicle.name }} ({{ vehicle.fuelConsumptionCombined }}{{ vehicle.fuelType === 'electric' ? 'kWh' : 'L' }}/100km)
                </ion-select-option>
              </ion-select>
            </div>
          </div>
        </div>

        <!-- Add New Trip Button -->
        <div v-if="trips.length > 0" class="new-trip-section">
          <!-- Premium Users: Always can add -->
          <ion-button 
            v-if="authStore.isPremium"
            expand="block" 
            @click="showTripList = false; showNewTripModal = true"
            class="new-trip-btn"
          >
            <ion-icon :icon="addOutline" slot="start"></ion-icon>
            {{ t('newTrip') }}
          </ion-button>
          
          <!-- Free Users: Limited to 1 trip -->
          <div v-else>
            <ion-button 
              v-if="trips.length < 1"
              expand="block" 
              @click="showTripList = false; showNewTripModal = true"
              class="new-trip-btn"
            >
              <ion-icon :icon="addOutline" slot="start"></ion-icon>
              {{ t('newTrip') }}
            </ion-button>
            
            <!-- Free tier limit reached -->
            <div v-else class="premium-limit-card">
              <div class="flex items-start gap-3">
                <div class="text-2xl">🔒</div>
                <div class="flex-1">
                  <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    {{ t('freeTierLimit') }}
                  </h4>
                  <p class="text-xs text-gray-600 dark:text-gray-400 mb-3">
                    {{ t('upgradeForUnlimitedTrips') }}
                  </p>
                  <ion-button 
                    size="small" 
                    @click="showTripList = false; router.push('/profile')"
                    class="upgrade-btn"
                  >
                    <ion-icon :icon="starOutline" slot="start"></ion-icon>
                    {{ t('upgradeToPremium') }}
                  </ion-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ion-content>
    </AppPanelModal>

    <!-- New Trip Modal -->
    <AppPanelModal :is-open="showNewTripModal" @did-dismiss="showNewTripModal = false">
      <ion-header>
        <ion-toolbar>
          <ion-title>{{ t('newTrip') }}</ion-title>
          <ion-buttons slot="end">
            <ion-button @click="showNewTripModal = false">{{ t('cancel') }}</ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding">
        <ion-item>
          <ion-label position="stacked">{{ t('tripName') }}</ion-label>
          <ion-input
            v-model="newTripName"
            :placeholder="t('tripNamePlaceholder')"
            @keyup.enter="createNewTrip"
            ref="tripNameInput"
          ></ion-input>
        </ion-item>
        <ion-button 
          expand="block" 
          @click="createNewTrip"
          :disabled="!newTripName.trim()"
          class="ion-margin-top"
        >
          {{ t('createTrip') }}
        </ion-button>
      </ion-content>
    </AppPanelModal>

    <!-- Menu Modal -->
    <AppPanelModal :is-open="showMenu" @did-dismiss="showMenu = false">
      <ion-header>
        <ion-toolbar>
          <ion-title>{{ t('menu') }}</ion-title>
          <ion-buttons slot="end">
            <ion-button @click="showMenu = false">
              <ion-icon :icon="closeOutline"></ion-icon>
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content>
        <ion-list>
          <!-- Vehicles -->
          <ion-item button @click="openVehiclesPage" lines="full">
            <ion-icon :icon="carSportOutline" slot="start"></ion-icon>
            <ion-label>{{ t('myVehicles') }}</ion-label>
            <ion-note slot="end">{{ vehicleStore.vehicles.length }}</ion-note>
          </ion-item>

          <!-- Statistics -->
          <ion-item button @click="openStatisticsPage" lines="full">
            <ion-icon :icon="statsChartOutline" slot="start"></ion-icon>
            <ion-label>{{ t('statistics') }}</ion-label>
          </ion-item>

          <!-- Trip History -->
          <ion-item button @click="openHistoryPage" lines="full">
            <ion-icon :icon="timeOutline" slot="start"></ion-icon>
            <ion-label>{{ t('tripHistory') }}</ion-label>
          </ion-item>

          <!-- Settings -->
          <ion-item button @click="openSettings" lines="full">
            <ion-icon :icon="settingsOutline" slot="start"></ion-icon>
            <ion-label>{{ t('settings') }}</ion-label>
          </ion-item>

          <!-- Privacy & Data -->
          <ion-item button @click="openPrivacyPage" lines="full">
            <ion-icon :icon="shieldCheckmarkOutline" slot="start"></ion-icon>
            <ion-label>{{ t('privacyAndData') }}</ion-label>
          </ion-item>

          <!-- Divider -->
          <div class="menu-divider"></div>

          <!-- Sign Out -->
          <ion-item button @click="handleSignOut" lines="none">
            <ion-icon :icon="logOutOutline" slot="start" color="danger"></ion-icon>
            <ion-label color="danger">{{ t('signOut') }}</ion-label>
          </ion-item>
        </ion-list>
      </ion-content>
    </AppPanelModal>

    <!-- Settings Modal -->
    <AppPanelModal :is-open="showSettingsModal" @did-dismiss="showSettingsModal = false">
      <ion-header>
        <ion-toolbar>
          <ion-buttons slot="start">
            <ion-button @click="backToMenu">
              <ion-icon :icon="arrowBackOutline"></ion-icon>
            </ion-button>
          </ion-buttons>
          <ion-title>{{ t('settings') }}</ion-title>
          <ion-buttons slot="end">
            <ion-button @click="showSettingsModal = false">{{ t('close') }}</ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content>
        <ion-list>
          <!-- Language Setting -->
          <ion-list-header>
            <ion-label>{{ t('languageNative') }}</ion-label>
          </ion-list-header>
          <ion-item>
            <ion-label>{{ t('language') }}</ion-label>
            <ion-select 
              v-model="selectedLanguage" 
              @ionChange="changeLanguage"
              interface="popover"
              :label="t('language')"
            >
              <ion-select-option value="en">🇬🇧 English</ion-select-option>
              <ion-select-option value="hu">🇭🇺 Magyar</ion-select-option>
              <ion-select-option value="de">🇩🇪 Deutsch</ion-select-option>
              <ion-select-option value="fr">🇫🇷 Français</ion-select-option>
              <ion-select-option value="it">🇮🇹 Italiano</ion-select-option>
              <ion-select-option value="es">🇪🇸 Español</ion-select-option>
              <ion-select-option value="pl">🇵🇱 Polski</ion-select-option>
              <ion-select-option value="ro">🇷🇴 Română</ion-select-option>
            </ion-select>
          </ion-item>

          <!-- Theme Setting -->
          <ion-list-header>
            <ion-label>{{ t('appearance') }}</ion-label>
          </ion-list-header>
          <ion-item>
            <ion-label>{{ t('theme') }}</ion-label>
            <ion-select 
              v-model="selectedTheme" 
              @ionChange="changeTheme"
              interface="popover"
              :label="t('theme')"
            >
              <ion-select-option value="light">
                ☀️ {{ t('themeLight') }}
              </ion-select-option>
              <ion-select-option value="dark">
                🌙 {{ t('themeDark') }}
              </ion-select-option>
              <ion-select-option value="auto">
                📱 {{ t('themeAuto') }}
              </ion-select-option>
            </ion-select>
          </ion-item>

          <!-- Map Language Setting -->
          <ion-list-header>
            <ion-label>{{ t('map') }}</ion-label>
          </ion-list-header>
          <ion-item>
            <ion-label>
              <h2>{{ t('mapLabels') }}</h2>
              <p>{{ t('cityAndStreetNames') }}</p>
            </ion-label>
            <ion-select 
              v-model="selectedMapLanguage" 
              @ionChange="changeMapLanguage"
              interface="popover"
              :label="t('mapLabels')"
            >
              <ion-select-option value="local">
                🌍 {{ t('localLanguage') }}
              </ion-select-option>
              <ion-select-option value="en">
                🇬🇧 {{ t('englishLanguage') }}
              </ion-select-option>
            </ion-select>
          </ion-item>

          <!-- Regional Settings -->
          <ion-list-header>
            <ion-label>{{ t('regional') }}</ion-label>
          </ion-list-header>
          
          <!-- Distance Unit -->
          <ion-item>
            <ion-label>{{ t('distanceUnit') }}</ion-label>
            <ion-select 
              v-model="selectedDistanceUnit" 
              @ionChange="changeDistanceUnit"
              interface="popover"
            >
              <ion-select-option value="km">Kilometers (km)</ion-select-option>
              <ion-select-option value="mi">Miles (mi)</ion-select-option>
            </ion-select>
          </ion-item>

          <!-- Speed Unit -->
          <ion-item>
            <ion-label>{{ t('speedUnit') }}</ion-label>
            <ion-select 
              v-model="selectedSpeedUnit" 
              @ionChange="changeSpeedUnit"
              interface="popover"
            >
              <ion-select-option value="kmh">km/h</ion-select-option>
              <ion-select-option value="mph">mph</ion-select-option>
            </ion-select>
          </ion-item>

          <!-- Fuel Consumption Unit -->
          <ion-item>
            <ion-label>{{ t('fuelUnit') }}</ion-label>
            <ion-select 
              v-model="selectedFuelUnit" 
              @ionChange="changeFuelUnit"
              interface="popover"
            >
              <ion-select-option value="l100km">L/100km</ion-select-option>
              <ion-select-option value="mpg">MPG (US)</ion-select-option>
              <ion-select-option value="kml">km/L</ion-select-option>
            </ion-select>
          </ion-item>

          <!-- Currency -->
          <ion-item>
            <ion-label>{{ t('currencySettings') }}</ion-label>
            <ion-select 
              v-model="selectedCurrency" 
              @ionChange="changeCurrency"
              interface="popover"
            >
              <ion-select-option value="EUR">€ Euro (EUR)</ion-select-option>
              <ion-select-option value="USD">$ US Dollar (USD)</ion-select-option>
              <ion-select-option value="GBP">£ British Pound (GBP)</ion-select-option>
              <ion-select-option value="HUF">Ft Hungarian Forint (HUF)</ion-select-option>
              <ion-select-option value="PLN">zł Polish Zloty (PLN)</ion-select-option>
              <ion-select-option value="RON">lei Romanian Leu (RON)</ion-select-option>
            </ion-select>
          </ion-item>

          <!-- Security Settings -->
          <ion-list-header>
            <ion-label>{{ t('security') }}</ion-label>
          </ion-list-header>
          
          <TwoFactorAuth />
        </ion-list>
      </ion-content>
    </AppPanelModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { 
  IonButton, 
  IonIcon, 
  IonHeader, 
  IonToolbar, 
  IonTitle, 
  IonButtons,
  IonContent,
  IonList,
  IonListHeader,
  IonItem,
  IonItemSliding,
  IonItemOptions,
  IonItemOption,
  IonLabel,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonNote,
  alertController
} from '@ionic/vue'
import { 
  mapOutline, 
  chevronDownOutline, 
  addOutline, 
  logOutOutline,
  checkmarkOutline,
  checkmarkCircleOutline,
  trashOutline,
  settingsOutline,
  sunnyOutline,
  moonOutline,
  phonePortraitOutline,
  carSportOutline,
  statsChartOutline,
  timeOutline,
  closeOutline,
  locationOutline,
  navigateOutline,
  menuOutline,
  shieldCheckmarkOutline,
  arrowBackOutline,
  starOutline
} from 'ionicons/icons'
import { storeToRefs } from 'pinia'
import { useTripStore } from '../stores/trip'
import { useAuthStore } from '../stores/auth'
import { useSettingsStore } from '../stores/settings'
import { useVehicleStore } from '../stores/vehicle'
import { useRouter } from 'vue-router'
import type { Trip } from '../types'
import { useI18n } from '../i18n'
import TwoFactorAuth from './TwoFactorAuth.vue'
import AppPanelModal from './AppPanelModal.vue'

const tripStore = useTripStore()
const authStore = useAuthStore()
const vehicleStore = useVehicleStore()
const settingsStore = useSettingsStore()
const router = useRouter()
const { t } = useI18n()

const { trips, currentTrip } = storeToRefs(tripStore)
const { user } = storeToRefs(authStore)
const { language, theme, mapLanguage, settings } = storeToRefs(settingsStore)

const showTripList = ref(false)
const showNewTripModal = ref(false)
const showMenu = ref(false)
const showSettingsModal = ref(false)
const newTripName = ref('')
const tripNameInput = ref()
const selectedLanguage = ref(language.value)
const selectedTheme = ref(theme.value)
const selectedMapLanguage = ref(mapLanguage.value)
const selectedDistanceUnit = ref(settings.value.distanceUnit || 'km')
const selectedSpeedUnit = ref(settings.value.speedUnit || 'kmh')
const selectedFuelUnit = ref(settings.value.fuelConsumptionUnit || 'l100km')
const selectedCurrency = ref(settings.value.currency || 'EUR')

const currentTripName = computed(() => {
  return currentTrip.value?.name || t('selectTrip')
})

// Focus input when modal opens
watch(showNewTripModal, (isOpen) => {
  if (isOpen) {
    newTripName.value = ''
    setTimeout(() => {
      tripNameInput.value?.$el.setFocus()
    }, 300)
  }
})

function changeLanguage(event: CustomEvent) {
  const newLanguage = event.detail.value
  settingsStore.setLanguage(newLanguage)
  selectedLanguage.value = newLanguage
}

function changeTheme(event: CustomEvent) {
  const newTheme = event.detail.value
  settingsStore.setTheme(newTheme)
  selectedTheme.value = newTheme
}

function changeMapLanguage(event: CustomEvent) {
  const newMapLang = event.detail.value
  settingsStore.setMapLanguage(newMapLang)
  selectedMapLanguage.value = newMapLang
}

function changeDistanceUnit(event: CustomEvent) {
  const newUnit = event.detail.value
  settingsStore.setDistanceUnit(newUnit)
  selectedDistanceUnit.value = newUnit
}

function changeSpeedUnit(event: CustomEvent) {
  const newUnit = event.detail.value
  settingsStore.setSpeedUnit(newUnit)
  selectedSpeedUnit.value = newUnit
}

function changeFuelUnit(event: CustomEvent) {
  const newUnit = event.detail.value
  settingsStore.setFuelConsumptionUnit(newUnit)
  selectedFuelUnit.value = newUnit
}

function changeCurrency(event: CustomEvent) {
  const newCurrency = event.detail.value
  settingsStore.setCurrency(newCurrency)
  selectedCurrency.value = newCurrency
}

function openSettings() {
  showMenu.value = false
  router.push('/settings')
}

function backToMenu() {
  showSettingsModal.value = false
  showMenu.value = true
}

function openPrivacyPage() {
  showMenu.value = false
  router.push('/privacy')
}

function openVehiclesPage() {
  showMenu.value = false
  router.push('/vehicles')
}

function openStatisticsPage() {
  showMenu.value = false
  router.push('/statistics')
}

function openHistoryPage() {
  showMenu.value = false
  router.push('/history')
}

async function selectTrip(trip: Trip) {
  await tripStore.loadTrip(trip.id)
  showTripList.value = false
}

async function handleVehicleChange(vehicleId: string | null) {
  if (!currentTrip.value) return
  
  // Get the selected vehicle
  const vehicle = vehicleId ? vehicleStore.vehicles.find(v => v.id === vehicleId) : null
  
  // Set default prices based on fuel type
  let defaultPrice = 1.5 // Default fuel price €/L
  if (vehicle?.fuelType === 'electric') {
    defaultPrice = 0.45 // Default electricity price €/kWh
  } else if (vehicle?.fuelType === 'diesel') {
    defaultPrice = 1.45 // Diesel is usually slightly cheaper
  } else if (vehicle?.fuelType === 'gasoline') {
    defaultPrice = 1.6 // Gasoline is usually slightly more expensive
  }
  
  // Update trip with new vehicle, its fuel consumption, and appropriate price
  await tripStore.updateTrip({
    vehicleId: vehicleId,
    fuelConsumption: vehicle?.fuelConsumptionCombined ?? 8.0,
    fuelPrice: defaultPrice
  })
  
  // Recalculate the trip with new fuel settings
  if (currentTrip.value.stops.length >= 2) {
    await tripStore.calculateTrip()
  }
}

async function handleTripVehicleChange(tripId: string, vehicleIdOrNone: string) {
  const vehicleId = vehicleIdOrNone === 'none' ? null : vehicleIdOrNone
  
  // Get the selected vehicle
  const vehicle = vehicleId ? vehicleStore.vehicles.find(v => v.id === vehicleId) : null
  
  // Set default prices based on fuel type
  let defaultPrice = 1.5 // Default fuel price €/L
  if (vehicle?.fuelType === 'electric') {
    defaultPrice = 0.45 // Default electricity price €/kWh
  } else if (vehicle?.fuelType === 'diesel') {
    defaultPrice = 1.45 // Diesel is usually slightly cheaper
  } else if (vehicle?.fuelType === 'gasoline') {
    defaultPrice = 1.6 // Gasoline is usually slightly more expensive
  }
  
  // Load the trip temporarily to update it
  const previousTripId = currentTrip.value?.id
  await tripStore.loadTrip(tripId)
  
  // Update trip with new vehicle, its fuel consumption, and appropriate price
  await tripStore.updateTrip({
    vehicleId: vehicleId,
    fuelConsumption: vehicle?.fuelConsumptionCombined ?? 8.0,
    fuelPrice: defaultPrice
  })
  
  // Recalculate if the trip has enough stops
  const trip = trips.value.find(t => t.id === tripId)
  if (trip && trip.stops.length >= 2) {
    await tripStore.calculateTrip()
  }
  
  // Restore previous trip if it was different
  if (previousTripId && previousTripId !== tripId) {
    await tripStore.loadTrip(previousTripId)
  }
}

function handleAddVehicle() {
  showTripList.value = false
  router.push('/profile?tab=vehicles')
}

async function createNewTrip() {
  if (!newTripName.value.trim() || !user.value) return
  
  try {
    await tripStore.createTrip(newTripName.value.trim(), user.value.id)
    showNewTripModal.value = false
    newTripName.value = ''
  } catch (error) {
    console.error('Failed to create trip:', error)
    alert('Failed to create trip. Please try again.')
  }
}

async function confirmDeleteTrip(trip: Trip) {
  const alert = await alertController.create({
    header: t('deleteTrip'),
    message: `${t('deleteConfirm')} "${trip.name}"? ${t('deleteMessage')} ${trip.stops.length} ${t('stops')}.`,
    buttons: [
      {
        text: t('cancel'),
        role: 'cancel'
      },
      {
        text: t('delete'),
        role: 'destructive',
        handler: () => deleteTrip(trip)
      }
    ]
  })

  await alert.present()
}

async function deleteTrip(trip: Trip) {
  try {
    await tripStore.deleteTrip(trip.id)
    
    // If we deleted the current trip and there are other trips, select the first one
    if (currentTrip.value?.id === trip.id && trips.value.length > 0) {
      await tripStore.loadTrip(trips.value[0].id)
    }
  } catch (error) {
    console.error('Failed to delete trip:', error)
    const errorAlert = await alertController.create({
      header: t('error'),
      message: 'Failed to delete trip. Please try again.',
      buttons: ['OK']
    })
    await errorAlert.present()
  }
}

async function handleSignOut() {
  showMenu.value = false
  await authStore.signOut()
  router.push('/auth')
}

function formatDuration(minutes: number): string {
  const roundedMinutes = Math.round(minutes)
  const hours = Math.floor(roundedMinutes / 60)
  const mins = roundedMinutes % 60
  if (hours > 0) {
    return `${hours}h ${mins}m`
  }
  return `${mins}m`
}

function formatDistance(meters: number): string {
  const km = meters / 1000
  return `${km.toFixed(1)} km`
}

</script>

<style scoped>
.trip-menu {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
}

.trip-selector {
  flex: 1;
}

.trip-button {
  --color: var(--ion-text-color);
  text-transform: none;
  font-size: 1rem;
}

.trip-name {
  margin: 0 0.5rem;
  font-weight: 500;
}

ion-modal ion-content {
  --background: var(--ion-background-color);
}

/* Vehicle Section */
.vehicle-section {
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--ion-color-light);
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  min-height: 300px;
}

.empty-icon {
  font-size: 4rem;
  color: var(--ion-color-medium);
  margin-bottom: 1rem;
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--ion-text-color);
  margin-bottom: 0.5rem;
}

.empty-subtitle {
  color: var(--ion-color-medium);
  margin-bottom: 1.5rem;
}

/* Trips Grid */
.trips-grid {
  display: grid;
  gap: 0.625rem;
  padding: 0.5rem 0;
}

/* Trip Card - Kompakt elrendezés */
.trip-card {
  background: var(--ion-card-background, #fff);
  border-radius: 10px;
  padding: 0;
  border: 2px solid transparent;
  transition: all 0.2s ease;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.trip-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.trip-card-content {
  padding: 0.75rem 0.875rem;
  cursor: pointer;
}

.trip-card.selected {
  border-color: var(--ion-color-primary);
  background: var(--ion-card-background, #fff);
  box-shadow: 0 2px 10px rgba(var(--ion-color-primary-rgb), 0.25);
}

.trip-card-content {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* Card Header - Minden egy sorban */
.trip-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.trip-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--ion-color-primary);
  border-radius: 9px;
  flex-shrink: 0;
}

.trip-icon {
  font-size: 1.375rem;
  color: white;
}

.trip-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1875rem;
}

.trip-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--ion-text-color);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.4;
}

/* Meta információk egy sorban */
.trip-meta {
  display: flex;
  align-items: center;
  gap: 0.4375rem;
  font-size: 0.8125rem;
  color: var(--ion-color-medium);
  line-height: 1.3;
}

.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 0.3125rem;
  white-space: nowrap;
}

.meta-icon {
  font-size: 0.9375rem;
  opacity: 0.8;
}

.meta-divider {
  opacity: 0.5;
  font-size: 0.6875rem;
}

/* Actions jobb oldalon */
.trip-actions {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-shrink: 0;
}

.selected-icon {
  font-size: 1.375rem;
  color: var(--ion-color-primary);
}

.delete-btn {
  --padding-start: 0.375rem;
  --padding-end: 0.375rem;
  margin: 0;
  height: 36px;
  width: 36px;
}

.delete-btn ion-icon {
  font-size: 1.125rem;
}

/* Vehicle Section per Trip */
.trip-vehicle-section {
  padding: 0.625rem 0.875rem;
  background: var(--ion-color-light);
  border-top: 1px solid var(--ion-color-light-shade);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.vehicle-label {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--ion-text-color);
  white-space: nowrap;
}

.vehicle-icon {
  font-size: 1rem;
  color: var(--ion-color-primary);
}

.vehicle-select {
  flex: 1;
  font-size: 0.8125rem;
  --padding-start: 0.5rem;
  --padding-end: 0.5rem;
}

/* Dark mode adjustments */
.dark .trip-card {
  background: var(--ion-card-background, #1e1e1e);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
}

.dark .trip-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
}

.dark .trip-vehicle-section {
  background: rgba(255, 255, 255, 0.05);
  border-top-color: rgba(255, 255, 255, 0.1);
}

/* New Trip Section */
.new-trip-section {
  padding: 1rem 0.5rem 0.5rem;
  border-top: 1px solid var(--ion-color-light);
  margin-top: 0.5rem;
}

.new-trip-btn {
  --background: var(--ion-color-primary);
  --background-hover: var(--ion-color-primary-shade);
  font-weight: 500;
}

.premium-limit-card {
  background: var(--ion-color-warning-tint);
  border: 1px solid var(--ion-color-warning);
  border-radius: 12px;
  padding: 1rem;
}

.dark .premium-limit-card {
  background: rgba(255, 200, 0, 0.1);
  border-color: rgba(255, 200, 0, 0.3);
}

.upgrade-btn {
  --background: var(--ion-color-warning);
  --background-hover: var(--ion-color-warning-shade);
  --color: #000;
  font-weight: 600;
  font-size: 0.75rem;
}

/* Legacy styles */
.selected-trip {
  --background: var(--ion-color-primary-tint);
}

ion-list-header {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.875rem;
  color: var(--ion-color-medium);
  padding-top: 1rem;
}

.menu-divider {
  height: 1px;
  background: var(--ion-color-light);
  margin: 1rem 0;
}

ion-list-header:first-of-type {
  padding-top: 0.5rem;
}
</style>
